// src/context/WalletContext.jsx

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { 
  detectWalletProviders, 
  signMessage, 
  getWalletProvider, 
  getChainInfo, 
  isSolanaWallet,
  getOkxSolanaProvider
} from '../utils/walletUtils';

import {
  tryConnectionFallbacks,
  tryAlternateApproaches
} from './walletConnectionHelpers';

import { handleOkxNetworkSwitch } from './walletNetworkHelpers';

// Create wallet context
const WalletContext = createContext(null);

// Custom hook for using the wallet context
export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

// Supported networks to display in wallet connector
const SUPPORTED_NETWORKS = [
  { id: 'ethereum', name: 'Ethereum', icon: '🔷' },
  { id: 'bsc', name: 'BNB Smart Chain', icon: '🟡' },
  { id: 'solana', name: 'Solana', icon: '🟣' },
];

// Wallet provider component
export const WalletProvider = ({ children }) => {
  const [wallet, setWallet] = useState({
    connected: false,
    address: null,
    type: null,
    balance: '0.00',
    usdtBalance: '0.00',
    chainId: null,
    chainName: null,
    network: null,
  });
  
  const [walletStatus, setWalletStatus] = useState({
    isConnecting: false,
    isInitialized: false,
    error: null,
    message: null,
  });
  
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [isSwitchNetworkModalOpen, setIsSwitchNetworkModalOpen] = useState(false);
  
  // Initialize wallet providers state
  const [availableWallets, setAvailableWallets] = useState({
    okx: false,
    binance: false,
    metamask: false,
    trust: false,
    coinbase: false,
    phantom: false,
    solflare: false,
    ledger: false,
    walletconnect: false,
    other: false
  });

  // Add this effect to detect available wallets
  useEffect(() => {
    const detectWallets = () => {
      const detected = detectWalletProviders();
      setAvailableWallets(detected);
    };
    
    detectWallets();
    
    // Redetect wallets if window changes
    window.addEventListener('focus', detectWallets);
    
    return () => {
      window.removeEventListener('focus', detectWallets);
    };
  }, []);
  
  // Function to show notification message
  const showNotification = useCallback((message, isError = false) => {
    if (isError) {
      setWalletStatus(prev => ({ ...prev, error: message, message: null }));
    } else {
      setWalletStatus(prev => ({ ...prev, message, error: null }));
    }
    
    // Auto-hide message after 4 seconds
    setTimeout(() => {
      setWalletStatus(prev => ({ ...prev, message: null, error: null }));
    }, 4000);
  }, []);
  
  // Initialize wallet from localStorage and check provider
  useEffect(() => {
    const initializeWallet = async () => {
      try {
        const savedWallet = localStorage.getItem('aerofi_wallet');
        
        if (savedWallet) {
          try {
            const parsedWallet = JSON.parse(savedWallet);
            
            // Check if wallet provider exists
            const isProviderAvailable = getWalletProvider(parsedWallet.type) !== null;
            
            if (isProviderAvailable) {
              // Try to reconnect to the wallet
              try {
                await reconnectWallet(parsedWallet);
              } catch (reconnectError) {
                console.warn('Failed to reconnect wallet:', reconnectError);
                // If reconnection fails, clear saved wallet
                localStorage.removeItem('aerofi_wallet');
                setWallet({
                  connected: false,
                  address: null,
                  type: null,
                  balance: '0.00',
                  usdtBalance: '0.00',
                  chainId: null,
                  chainName: null,
                  network: null,
                });
              }
            } else {
              // If provider is not available, update state but keep data
              setWallet({
                ...parsedWallet,
                connected: false,
              });
            }
          } catch (error) {
            console.error('Failed to parse saved wallet:', error);
            localStorage.removeItem('aerofi_wallet');
          }
        }
        
        setWalletStatus(prev => ({
          ...prev,
          isInitialized: true,
        }));
      } catch (error) {
        console.error('Error initializing wallet:', error);
        setWalletStatus(prev => ({
          ...prev,
          isInitialized: true,
          error: 'Failed to initialize wallet',
        }));
      }
    };
    
    initializeWallet();
  }, []);
  
  // Try to reconnect to previously connected wallet
  const reconnectWallet = async (savedWallet) => {
    try {
      setWalletStatus(prev => ({ ...prev, isConnecting: true }));
      
      // Special handling for OKX with Solana chain
      const isOkxSolana = savedWallet.type.toLowerCase() === 'okx' && savedWallet.network === 'solana';
      const provider = isOkxSolana ? 
        getOkxSolanaProvider() : 
        getWalletProvider(savedWallet.type);
      
      if (!provider) {
        throw new Error('Wallet provider not available');
      }
      
      // Check if still connected by requesting accounts
      let accounts;
      try {
        // Handle different wallet types differently
        if (isSolanaWallet(savedWallet.address) || savedWallet.network === 'solana') {
          // OKX Solana specific handling
          if (isOkxSolana) {
            try {
              // Check if already connected
              if (provider.isConnected && provider.publicKey) {
                accounts = [provider.publicKey.toString()];
              } else {
                // Try to connect
                const connection = await provider.connect();
                if (connection && connection.publicKey) {
                  accounts = [connection.publicKey.toString()];
                } else if (provider.publicKey) {
                  accounts = [provider.publicKey.toString()];
                } else {
                  throw new Error('Could not get OKX Solana address');
                }
              }
            } catch (err) {
              console.error('OKX Solana reconnection error:', err);
              throw new Error('Failed to reconnect to OKX Solana wallet');
            }
          } else if (savedWallet.type.toLowerCase() === 'phantom') {
            // Phantom specific handling
            if (!provider) {
              throw new Error('Phantom wallet provider not found');
            }
            
            if (provider.isConnected) {
              if (provider.publicKey) {
                accounts = [provider.publicKey.toString()];
              } else {
                throw new Error('Phantom wallet connected but address not available');
              }
            } else {
              try {
                const connection = await provider.connect();
                if (connection && connection.publicKey) {
                  accounts = [connection.publicKey.toString()];
                } else {
                  throw new Error('Failed to get Phantom wallet address');
                }
              } catch (err) {
                if (err.code === 4001) {
                  throw new Error('Connection request rejected by user');
                } else {
                  throw new Error(`Phantom wallet connection error: ${err.message || 'Unknown error'}`);
                }
              }
            }
          } else if (savedWallet.type.toLowerCase() === 'solflare') {
            // Solflare specific handling
            if (provider.publicKey) {
              accounts = [provider.publicKey.toString()];
            } else {
              const connection = await provider.connect();
              accounts = [connection.publicKey.toString()];
            }
          } else {
            // Generic Solana handling
            if (provider.publicKey) {
              accounts = [provider.publicKey.toString()];
            } else {
              throw new Error('Could not reconnect to Solana wallet');
            }
          }
        } else {
          // Standard EVM handling
          accounts = await provider.request({ method: 'eth_accounts' });
          
          // If no accounts, try requesting again
          if (!accounts || accounts.length === 0) {
            accounts = await provider.request({ method: 'eth_requestAccounts' });
          }
        }
      } catch (error) {
        console.warn('Failed to get accounts:', error);
        throw new Error('Failed to reconnect to wallet');
      }
      
      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts available');
      }
      
      // Check if the account matches the saved one, if not update it
      const currentAddress = accounts[0];
      
      // Get chain information with the proper chain preference for OKX
      const chainInfo = await getChainInfo(
        provider, 
        savedWallet.type, 
        isOkxSolana ? 'solana' : null
      );

      // Update wallet state with fresh data
      const updatedWallet = {
        connected: true,
        address: currentAddress,
        type: savedWallet.type,
        balance: chainInfo.balance,
        usdtBalance: chainInfo.usdtBalance || '0.00',
        chainId: chainInfo.chainId,
        chainName: chainInfo.name,
        network: chainInfo.network,
      };
      
      setWallet(updatedWallet);
      
      // Update localStorage
      localStorage.setItem('aerofi_wallet', JSON.stringify(updatedWallet));
      
      return true;
    } catch (error) {
      console.error('Reconnection error:', error);
      throw error;
    } finally {
      setWalletStatus(prev => ({ ...prev, isConnecting: false }));
    }
  };
  
  // Disconnect wallet
  const disconnectWallet = useCallback(() => {
    // First save the wallet type for a nice notification message
    const walletType = wallet.type;
    
    setWallet({
      connected: false,
      address: null,
      type: null,
      balance: '0.00',
      usdtBalance: '0.00',
      chainId: null,
      chainName: null,
      network: null,
    });
    
    // Show notification
    if (walletType) {
      showNotification(`Disconnected from ${walletType} wallet`);
    }
    
    // Remove from localStorage
    localStorage.removeItem('aerofi_wallet');
  }, [wallet.type, showNotification]);
  
  // Connect wallet from scratch - Enhanced for OKX Solana support
  const connectWallet = useCallback(async (walletType, preferredChain = null) => {
    // Validate wallet type
    if (!walletType) {
      console.error('No wallet type specified');
      return null;
    }
    
    console.log('Connecting wallet:', walletType, 'with preferred chain:', preferredChain);
    
    try {
      setWalletStatus(prev => ({ ...prev, isConnecting: true, error: null }));
      
      // Special handling for OKX with Solana chain preference
      let provider;
      if (walletType.toLowerCase() === 'okx' && preferredChain === 'solana') {
        // Get the OKX Solana provider using our optimized function
        provider = getOkxSolanaProvider();
        
        // If provider still not found, try fallback methods
        if (!provider) {
          // Try to get from window.okxwallet directly to ensure we didn't miss it
          if (window.okxwallet) {
            console.log('Attempting to access Solana provider directly from okxwallet...');
            
            // Try looking for solana property with different casing
            const okxProps = Object.keys(window.okxwallet);
            const solanaProps = okxProps.filter(prop => prop.toLowerCase().includes('solana'));
            
            if (solanaProps.length > 0) {
              provider = window.okxwallet[solanaProps[0]];
              console.log(`Found Solana provider at window.okxwallet.${solanaProps[0]}`);
            } else {
              // Maybe it's a property inside okxwallet
              for (const key of okxProps) {
                try {
                  const value = window.okxwallet[key];
                  if (value && typeof value === 'object' && value.connect && typeof value.connect === 'function') {
                    console.log(`Found potential provider at window.okxwallet.${key}`);
                    provider = value;
                    break;
                  }
                } catch (e) {
                  // Skip inaccessible properties
                }
              }
            }
          }
          
          // If still no provider, check if we have window.solana with OKX flags
          if (!provider && window.solana && (window.solana.isOkx || window.solana.isOKX)) {
            console.log('Found OKX-branded Solana provider at window.solana');
            provider = window.solana;
          }
          
          // If still no provider, show a user-friendly error
          if (!provider) {
            throw new Error(
              'OKX wallet detected, but Solana support is not enabled. Please open your OKX wallet, ' +
              'go to Settings → Networks, and make sure Solana is enabled. Then create or import a Solana account.'
            );
          }
        }
      } else {
        // For other wallets, use regular getWalletProvider
        provider = getWalletProvider(walletType, preferredChain);
      }
      
      if (!provider) {
        throw new Error(`${walletType} wallet provider not available`);
      }
      
      console.log('Provider obtained:', provider);
      
      // Request accounts based on wallet type
      let accounts;
      
      // Handle based on wallet type and chain preference
      if (walletType.toLowerCase() === 'okx' && preferredChain === 'solana') {
        // OKX Solana specific handling with improved reliability
        try {
          console.log('Connecting to OKX Solana wallet using enhanced method...');
          
          // First try a silent check if already connected (most efficient path)
          if (provider.isConnected && provider.publicKey) {
            const pubKey = provider.publicKey.toString ? 
              provider.publicKey.toString() : 
              provider.publicKey;
            accounts = [pubKey];
            console.log('OKX Solana wallet already connected:', accounts);
          } else {
            // Try direct connection first
            try {
              console.log('Attempting primary connect() method for OKX Solana');
              
              // First check if we have a connect method
              if (typeof provider.connect !== 'function') {
                console.warn('OKX Solana provider missing connect method, trying to find alternatives');
                accounts = await tryAlternateApproaches(provider);
                if (!accounts) {
                  throw new Error('OKX Solana provider does not have a connect method');
                }
              } else {
                // Standard connect with auto-detection of response format
                const resp = await provider.connect();
                console.log('OKX Solana connection response:', resp);
                
                // Handle different response formats
                if (resp === null) {
                  // Some OKX versions return null on success but update the provider
                  if (provider.publicKey) {
                    const pubKey = provider.publicKey.toString ? 
                      provider.publicKey.toString() : 
                      provider.publicKey;
                    accounts = [pubKey];
                    console.log('NULL connect response but publicKey available on provider:', accounts);
                  }
                } else if (resp) {
                  // Handle various response formats
                  if (resp.publicKey) {
                    // Standard format with publicKey in response
                    const pubKey = resp.publicKey.toString ? resp.publicKey.toString() : resp.publicKey;
                    accounts = [pubKey];
                    console.log('Got publicKey from connect response object:', accounts);
                  } else if (typeof resp === 'string') {
                    // Direct string address response
                    accounts = [resp];
                    console.log('Connect returned string address:', accounts);
                  } else if (resp.data?.publicKey || resp.result?.publicKey || resp.key) {
                    // Nested response formats
                    const key = (resp.data?.publicKey || resp.result?.publicKey || resp.key);
                    accounts = [key.toString ? key.toString() : key];
                    console.log('Found publicKey in nested response property:', accounts);
                  } else if (typeof resp.toString === 'function') {
                    // Response is an object with toString
                    try {
                      const strValue = resp.toString();
                      if (strValue.length > 30) { // Likely a valid address if long enough
                        accounts = [strValue];
                        console.log('Found string representation of response:', accounts);
                      }
                    } catch (e) {
                      // Ignore toString errors
                    }
                  }
                }
                
                // If still no accounts, check if publicKey is on provider after connect
                if (!accounts || accounts.length === 0) {
                  if (provider.publicKey) {
                    const pubKey = provider.publicKey.toString ? 
                      provider.publicKey.toString() : 
                      provider.publicKey;
                    accounts = [pubKey];
                    console.log('Found publicKey on provider after connect attempt:', accounts);
                  } else {
                    // If still no accounts, try our fallback methods
                    accounts = await tryConnectionFallbacks(provider);
                  }
                }
              }
            } catch (connError) {
              console.warn('Primary connect method failed:', connError);
              
              // Try our fallback connection methods
              accounts = await tryConnectionFallbacks(provider);
              
              // If still no accounts, try alternative approaches
              if (!accounts || accounts.length === 0) {
                accounts = await tryAlternateApproaches(provider);
              }
            }
            
            // Final check - if we still don't have accounts
            if (!accounts || accounts.length === 0) {
              throw new Error('Could not retrieve Solana public key after all connection attempts');
            }
          }
        } catch (err) {
          console.error('Error connecting to OKX Solana:', err);
          
          // Provide user-friendly error message
          if (err.message.includes('User rejected') || 
              err.message.includes('not authorized') || 
              err.message.includes('not approved')) {
            throw new Error('Please approve the connection request in your OKX wallet');
          } else if (err.message.includes('Could not retrieve Solana public key')) {
            throw new Error('Cannot access Solana account. Please ensure Solana is enabled in OKX wallet settings and you have created a Solana account.');
          } else {
            throw new Error(`Failed to connect OKX Solana wallet: ${err.message}`);
          }
        }
      } else if (walletType.toLowerCase() === 'phantom') {
        // Phantom specific connection with improved error handling
        try {
          // Check if Phantom is properly installed
          if (!provider || !provider.isPhantom) {
            throw new Error('Phantom wallet not detected or properly installed');
          }
          
          // Check if already connected
          if (provider.isConnected && provider.publicKey) {
            accounts = [provider.publicKey.toString()];
            console.log('Phantom wallet already connected:', accounts);
          } else {
            // Try to connect with proper error handling
            try {
              const connection = await provider.connect();
              if (connection && connection.publicKey) {
                accounts = [connection.publicKey.toString()];
                console.log('Connected to Phantom wallet:', accounts);
              } else {
                throw new Error('Failed to retrieve Phantom wallet address');
              }
            } catch (connectErr) {
              // Handle specific Phantom error codes
              if (connectErr.code === 4001) {
                throw new Error('Connection request was rejected by user');
              } else if (connectErr.message && connectErr.message.includes('already connected')) {
                // If already connected, try to get public key directly
                if (provider.publicKey) {
                  accounts = [provider.publicKey.toString()];
                } else {
                  throw new Error('Phantom wallet connected but address not available');
                }
              } else {
                throw new Error(`Failed to connect Phantom wallet: ${connectErr.message}`);
              }
            }
          }
        } catch (phantomErr) {
          console.error('Phantom wallet connection error:', phantomErr);
          throw phantomErr; // Rethrow for consistent error handling
        }
      } else if (isSolanaWallet(walletType)) {
        // Regular Solana wallets (other than Phantom)
        if (walletType.toLowerCase() === 'solflare') {
          // Solflare specific connection
          try {
            const connection = await provider.connect();
            accounts = [connection.publicKey.toString()];
          } catch (err) {
            throw new Error(`Failed to connect Solflare wallet: ${err.message}`);
          }
        } else {
          // Generic Solana connection
          try {
            if (provider.connect) {
              const connection = await provider.connect();
              accounts = [connection.publicKey.toString()];
            } else if (provider.publicKey) {
              accounts = [provider.publicKey.toString()];
            } else {
              throw new Error('Unsupported Solana wallet');
            }
          } catch (err) {
            throw new Error(`Failed to connect Solana wallet: ${err.message}`);
          }
        }
      } else {
        // EVM wallets (Ethereum, BSC, OKX with EVM, etc)
        try {
          accounts = await provider.request({ method: 'eth_requestAccounts' });
        } catch (err) {
          throw new Error(`Failed to connect ${walletType} wallet: ${err.message}`);
        }
      }
      
      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found or permission denied');
      }
      
      // Get current address
      const currentAddress = accounts[0];
      
      // Get chain information - pass preferredChain for OKX wallet
      const chainInfo = await getChainInfo(
        provider, 
        walletType,
        preferredChain
      );
      
      // Update wallet state with connected wallet info
      const updatedWallet = {
        connected: true,
        address: currentAddress,
        type: walletType,
        balance: chainInfo.balance,
        usdtBalance: chainInfo.usdtBalance || '0.00',
        chainId: chainInfo.chainId,
        chainName: chainInfo.name,
        network: chainInfo.network,
      };
      
      setWallet(updatedWallet);
      
      // Show success notification with chain info for OKX
      if (walletType.toLowerCase() === 'okx' && preferredChain === 'solana') {
        showNotification(`Successfully connected to OKX wallet (Solana)`);
      } else if (walletType.toLowerCase() === 'okx' && preferredChain === 'ethereum') {
        showNotification(`Successfully connected to OKX wallet (Ethereum)`);
      } else {
        showNotification(`Successfully connected to ${walletType} wallet`);
      }
      
      // Save to localStorage
      localStorage.setItem('aerofi_wallet', JSON.stringify(updatedWallet));
      
      // Close modal
      setIsWalletModalOpen(false);
      
      return updatedWallet;
    } catch (error) {
      console.error('Wallet connection error:', error);
      setWalletStatus(prev => ({ 
        ...prev, 
        error: `Connection error: ${error.message}` 
      }));
      return null;
    } finally {
      setWalletStatus(prev => ({ ...prev, isConnecting: false }));
    }
  }, [showNotification]);
  
  // Import the switchNetwork function from the helper file
  const switchNetwork = useCallback(async (chainId) => {
    try {
      if (!wallet.connected) {
        throw new Error('No wallet connected');
      }
      
      // Get the appropriate provider based on current wallet type and network
      const isCurrentlySolana = wallet.network === 'solana';
      const provider = isCurrentlySolana && wallet.type.toLowerCase() === 'okx' ?
        getOkxSolanaProvider() : 
        getWalletProvider(wallet.type);
      
      if (!provider) {
        throw new Error('Wallet provider not available');
      }
      
      // Check if wallet is a Solana wallet
      if (isSolanaWallet(wallet.type) || isCurrentlySolana) {
        if (chainId === 'solana') {
          // Already on Solana
          showNotification('Already connected to Solana network');
          return true;
        } else {
          // Solana wallets can't switch to EVM chains
          throw new Error('Solana wallets cannot switch to Ethereum or BSC networks. Please connect an EVM-compatible wallet.');
        }
      }
      
      // Check if user is trying to switch to Solana with a non-Solana wallet
      if (chainId === 'solana' && !isSolanaWallet(wallet.type)) {
        // Special case for OKX which supports both chains
        if (wallet.type.toLowerCase() === 'okx') {
          // For OKX, we need to disconnect and reconnect with Solana preference
          disconnectWallet();
          setTimeout(() => {
            // After disconnect, initiate a new connection with Solana preference
            connectWallet('okx', 'solana');
          }, 1000);
          return true;
        } else {
          throw new Error('Current wallet doesn\'t support Solana. Please connect a Solana-compatible wallet like Phantom.');
        }
      }
      
      // Check if wallet is already on the requested network
      if (wallet.network === chainId) {
        showNotification(`Already connected to ${chainId}`);
        return true;
      }
      
      // Special handling for OKX wallet
      if (wallet.type.toLowerCase() === 'okx') {
        return await handleOkxNetworkSwitch(chainId, wallet, showNotification, disconnectWallet, connectWallet, setWallet);
      }
      
      // Regular EVM compatible wallets
      // Common networks
      const networks = {
        ethereum: {
          chainId: '0x1', // 1
          chainName: 'Ethereum Mainnet',
          nativeCurrency: {
            name: 'Ether',
            symbol: 'ETH',
            decimals: 18
          },
          rpcUrls: ['https://mainnet.infura.io/v3/'],
          blockExplorerUrls: ['https://etherscan.io/']
        },
        bsc: {
          chainId: '0x38', // 56
          chainName: 'Binance Smart Chain',
          nativeCurrency: {
            name: 'BNB',
            symbol: 'BNB',
            decimals: 18
          },
          rpcUrls: ['https://bsc-dataseed.binance.org/'],
          blockExplorerUrls: ['https://bscscan.com/']
        }
      };
      
      // Try to switch to the network
      try {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: networks[chainId].chainId }],
        });
      } catch (switchError) {
        // This error code means that the chain has not been added to the wallet
        if (switchError.code === 4902) {
          try {
            await provider.request({
              method: 'wallet_addEthereumChain',
              params: [networks[chainId]],
            });
          } catch (addError) {
            throw new Error(`Failed to add network: ${addError.message}`);
          }
        } else {
          throw switchError;
        }
      }
      
      // Get updated chain information
      const chainInfo = await getChainInfo(provider, wallet.type);
      
      // Update wallet state
      const updatedWallet = {
        ...wallet,
        chainId: chainInfo.chainId,
        chainName: chainInfo.name,
        network: chainInfo.network,
        balance: chainInfo.balance,
        usdtBalance: chainInfo.usdtBalance || '0.00',
      };
      
      setWallet(updatedWallet);
      
      // Update localStorage
      localStorage.setItem('aerofi_wallet', JSON.stringify(updatedWallet));
      
      // Show success notification
      showNotification(`Switched to ${chainInfo.name}`);
      
      return true;
    } catch (error) {
      console.error('Failed to switch network:', error);
      showNotification(`Failed to switch network: ${error.message}`, true);
      
      // Re-throw the error so the calling component can display it
      throw error;
    }
  }, [wallet, showNotification, connectWallet, disconnectWallet]);
  
  // Open wallet modal
  const openWalletModal = useCallback(() => {
    setIsWalletModalOpen(true);
  }, []);
  
  // Close wallet modal
  const closeWalletModal = useCallback(() => {
    setIsWalletModalOpen(false);
  }, []);
  
  // Open switch network modal
  const openSwitchNetworkModal = useCallback(() => {
    setIsSwitchNetworkModalOpen(true);
  }, []);
  
  // Close switch network modal
  const closeSwitchNetworkModal = useCallback(() => {
    setIsSwitchNetworkModalOpen(false);
  }, []);
  
  const value = {
    wallet,
    walletStatus,
    isWalletModalOpen,
    isSwitchNetworkModalOpen,
    availableWallets,
    supportedNetworks: SUPPORTED_NETWORKS,
    connectWallet,
    disconnectWallet,
    openWalletModal,
    closeWalletModal,
    openSwitchNetworkModal,
    closeSwitchNetworkModal,
    switchNetwork,
    showNotification,
  };
  
  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

// Export the context and provider
export default WalletContext;
